// src/hooks/use-modal-back-handler.ts

import { useRef, useEffect } from 'react';

type Options = {
  enabled?: boolean;
};

/**
 * 전역 모달 카운터: 현재 열려있는 모달의 개수
 * 중첩 모달 시나리오를 처리하기 위해 사용
 * - 첫 번째 모달만 히스토리에 엔트리 추가
 * - 마지막 모달이 닫힐 때만 히스토리 cleanup
 */
let openModalCount = 0;

/**
 * 전역 모달 카운터 리셋 함수
 * 새로고침 후 또는 페이지 이동 시 카운터를 리셋하기 위해 사용
 */
export const resetOpenModalCount = () => {
  openModalCount = 0;
};

/**
 * 히스토리 cleanup 진행 중 플래그
 * history.back() 호출 시 popstate 이벤트가 재발생하는 것을 방지하기 위해 사용
 * - true: 현재 히스토리 cleanup이 진행 중
 * - false: cleanup이 진행되지 않음
 */
let isCleaningUp = false;

/**
 * 다음 UI 닫기에서 모달 히스토리 cleanup을 건너뛰기 위한 전역 플래그.
 *
 * 일반 모달은 닫힐 때 `window.history.back()`으로 모달용 history entry를 제거한다.
 * 다만 일부 플로우는 확인 버튼에서 직접 `window.history.go(...)`를 호출해
 * 모달 entry와 실패 route entry를 한 번에 되돌려야 한다. 이때 기본 cleanup까지 실행되면
 * history back이 중복되어 의도보다 더 이전 화면으로 이동할 수 있으므로,
 * 해당 플로우가 직접 history를 제어하기 직전에 이 함수를 호출한다.
 */
let shouldSkipNextHistoryCleanup = false;

export const skipNextModalHistoryCleanup = () => {
  shouldSkipNextHistoryCleanup = true;
};

/**
 * 모달 열기 처리 함수
 *
 * 역할:
 * - 전역 모달 카운터를 증가시킴
 * - 첫 번째 모달인 경우에만 브라우저 히스토리에 엔트리 추가
 * - 중첩 모달(두 번째 이후)은 히스토리 엔트리를 추가하지 않음
 *
 * 동작 시나리오:
 * 1. 첫 번째 모달 열기:
 *    - openModalCount: 0 → 1
 *    - window.history.pushState() 실행
 *    - pushedRef.current = true 설정
 *
 * 2. 중첩 모달 열기:
 *    - openModalCount: 1 → 2 (또는 그 이상)
 *    - 히스토리 push 안 함 (첫 번째 모달이 이미 push했으므로)
 *    - pushedRef.current = false 유지
 *
 * @param pushedRef - 이 모달이 히스토리를 push했는지 추적하는 ref
 */
const handleModalOpen = (pushedRef: React.MutableRefObject<boolean>) => {
  const isFirstModal = openModalCount === 0;

  // 첫 번째 모달인 경우에만 히스토리에 엔트리 추가
  if (isFirstModal) {
    /**
     * 현재 URL은 그대로 유지하고, state만 모달 표시로 push
     * - URL 변경 없음: window.location.pathname은 그대로
     * - state에 __modal: true 설정: 나중에 이 엔트리가 모달용인지 확인 가능
     * - 빈 문자열(''): 브라우저가 현재 URL을 그대로 사용
     */
    window.history.pushState({ __modal: true }, '');
    pushedRef.current = true;
  }
  // 모든 모달에 대해 카운터 증가
  openModalCount += 1;
};

/**
 * 모달 닫기 처리 함수
 *
 * 역할:
 * - 전역 모달 카운터를 감소시킴
 * - 마지막 모달이 닫힐 때만 브라우저 히스토리 cleanup 실행
 * - 중첩 모달이 있으면 히스토리는 유지하고 pushedRef만 초기화
 *
 * 동작 시나리오:
 * 1. 중첩 모달이 닫힐 때 (예: 두 번째 모달):
 *    - openModalCount: 2 → 1
 *    - pushedRef.current가 false이므로 히스토리 cleanup 안 함
 *    - 첫 번째 모달이 아직 열려있으므로 히스토리 유지
 *
 * 2. 마지막 모달이 닫힐 때 (예: 첫 번째 모달):
 *    - openModalCount: 1 → 0
 *    - pushedRef.current가 true이고 openModalCount가 0이면
 *    - window.history.back() 실행하여 모달용 히스토리 엔트리 제거
 *    - pushedRef.current = false로 초기화
 *
 * 3. 중첩 모달이 있지만 이 모달이 히스토리를 push한 경우:
 *    - openModalCount: 2 → 1 (아직 0이 아님)
 *    - pushedRef만 초기화하고 히스토리는 유지
 *    - 나중에 마지막 모달이 닫힐 때 cleanup 실행됨
 *
 * @param pushedRef - 이 모달이 히스토리를 push했는지 추적하는 ref
 */
const handleModalClose = (pushedRef: React.MutableRefObject<boolean>) => {
  // 이미 cleanup이 진행 중이면 무시 (중복 실행 방지)
  if (isCleaningUp) return;

  // 전역 카운터 감소 (안전장치: 음수 방지)
  openModalCount = Math.max(0, openModalCount - 1);

  /**
   * 호출부가 직접 history.go/back을 수행하는 특수 플로우.
   * 이 경우 모달 훅의 기본 cleanup이 다시 history.back()을 호출하면
   * 사용자가 기대한 이전 화면보다 한 단계 더 뒤로 이동할 수 있다.
   */
  if (shouldSkipNextHistoryCleanup) {
    shouldSkipNextHistoryCleanup = false;
    if (pushedRef.current) {
      pushedRef.current = false;
    }
    return;
  }

  // 마지막 모달인지 확인
  const isLastModal = openModalCount === 0;
  // 히스토리에 모달 엔트리가 있는지 확인
  const hasModalHistory = window.history.state?.__modal;

  /**
   * 마지막 모달이 닫히고, 이 모달이 히스토리를 push했으며,
   * 히스토리에 모달 엔트리가 남아있는 경우
   * → history.back()으로 히스토리 엔트리 제거
   *
   * 주의:
   * - history.back() 호출 시 popstate 이벤트가 발생함
   * - isCleaningUp 플래그를 설정하여 popstate 핸들러에서 중복 처리 방지
   */
  if (isLastModal && hasModalHistory && pushedRef.current) {
    isCleaningUp = true; // cleanup 플래그 설정
    window.history.back();
    pushedRef.current = false;
    // 플래그는 popstate 핸들러에서 리셋됨
    return;
  }

  /**
   * 중첩 모달이 있는 경우 (openModalCount > 0)
   * 이 모달이 히스토리를 push했다면 pushedRef만 초기화
   * 히스토리는 그대로 유지 (첫 번째 모달이 아직 열려있으므로)
   */
  if (pushedRef.current) {
    pushedRef.current = false;
  }
};

/**
 * popstate 이벤트 핸들러 생성 함수
 *
 * 역할:
 * - 브라우저 뒤로가기 버튼 클릭 시 발생하는 popstate 이벤트를 처리
 * - 모달이 열려있으면 모달만 닫기 (원래 기능)
 * - 라우트는 변경하지 않고 모달만 닫는 것이 목적
 *
 * popstate 이벤트가 발생하는 경우:
 * - Android 뒤로가기 버튼 클릭
 * - iOS 스와이프 제스처 (뒤로가기)
 * - 브라우저 뒤로가기 버튼 클릭
 * - history.back(), history.go(-1) 등 프로그래밍 방식 호출
 *
 * 동작 원리:
 * 1. popstate 이벤트 발생
 * 2. cleanup이 진행 중이면 플래그만 리셋하고 종료
 * 3. 모달이 열려있는지 확인 (openRef.current)
 * 4. 모달이 열려있으면:
 *    - 카운터 감소
 *    - pushedRef 초기화
 *    - onClose 호출하여 모달 닫기
 *    - 마지막 모달이면 히스토리 cleanup 처리
 *
 * 주의사항:
 * - React Router도 popstate 이벤트를 감지하여 라우트를 변경할 수 있음
 * - 하지만 여기서는 라우트 복원을 하지 않고 모달만 닫음
 * - 이는 의도된 동작 (모달이 URL을 바꾸지 않으므로)
 *
 * @param openRef - 최신 open 상태를 참조하는 ref (클로저 문제 해결)
 * @param onCloseRef - 최신 onClose 함수를 참조하는 ref (클로저 문제 해결)
 * @param pushedRef - 이 모달이 히스토리를 push했는지 추적하는 ref
 * @returns popstate 이벤트 핸들러 함수
 */
const createPopStateHandler =
  (
    openRef: React.MutableRefObject<boolean>,
    onCloseRef: React.MutableRefObject<() => void>,
    pushedRef: React.MutableRefObject<boolean>
  ) =>
  () => {
    /**
     * cleanup이 진행 중인 경우
     * - handleModalClose에서 history.back()을 호출했을 때 발생한 popstate
     * - 플래그만 리셋하고 모달은 이미 닫혔으므로 추가 처리 불필요
     */
    if (isCleaningUp) {
      isCleaningUp = false; // 플래그 리셋
      return;
    }

    /**
     * 모달을 닫아야 하는지 확인
     * - 모달이 열려있으면 항상 닫기 (원래 기능)
     * - pushedRef와 관계없이 모든 모달이 닫혀야 함
     */
    if (!openRef.current) return;

    // 이 모달이 히스토리를 push했는지 확인 (나중에 cleanup에 사용)
    const didPushHistory = pushedRef.current;

    // 모달 닫기 처리
    pushedRef.current = false;
    openModalCount = Math.max(0, openModalCount - 1);

    // 카운터 감소 후 마지막 모달인지 확인
    const isLastModal = openModalCount === 0;

    // 모달 닫기 함수 호출
    onCloseRef.current();

    /**
     * 히스토리 cleanup 처리
     * - 이 모달이 히스토리를 push했고, 마지막 모달이면 히스토리 상태 확인
     * - onClose() 호출 후에 실행하여 모달이 완전히 닫힌 후 처리
     *
     * 주의:
     * - 뒤로가기 버튼 클릭 시 브라우저가 히스토리를 뒤로 이동함
     * - 하지만 모달 엔트리의 state가 남아있을 수 있으므로 확인 필요
     * - 이미 popstate로 뒤로 이동했으므로 추가 history.back() 호출 불필요
     */
    if (didPushHistory && isLastModal) {
      // 히스토리 상태 확인 (안전장치)
      // popstate로 이미 뒤로 이동했으므로 추가 처리 불필요
      isCleaningUp = false; // 플래그 리셋 (안전장치)
    }
  };

/**
 * 모달의 브라우저 뒤로가기 버튼 처리를 위한 훅
 *
 * 요구사항:
 * - 모달 open 상태에서 Android back / iOS back 제스처 => 모달만 닫기
 * - 모달이 없으면 기존 router back 그대로
 * - 모달은 URL을 바꾸지 않음
 *
 * 동작 원리:
 * 1. 모달이 열릴 때: 첫 번째 모달인 경우에만 히스토리에 엔트리 추가
 * 2. 뒤로가기 버튼 클릭: popstate 이벤트 발생 → 모달이 열려있으면 모달만 닫기
 * 3. UI로 모달 닫기: 마지막 모달이 닫힐 때만 history.back()으로 히스토리 엔트리 제거
 *
 * 중첩 모달 처리:
 * - 첫 번째 모달만 히스토리에 엔트리 추가
 * - 중첩 모달이 열려있으면 첫 번째 모달의 cleanup을 건너뜀
 * - 마지막 모달이 닫힐 때만 히스토리 cleanup 실행
 *
 * @param open - 모달의 열림/닫힘 상태
 * @param onClose - 모달을 닫을 때 호출되는 함수
 * @param options - 선택적 옵션
 * @param options.enabled - 훅 활성화 여부 (기본값: true)
 */
export function useModalBackHandler(open: boolean, onClose: () => void, options?: Options) {
  const enabled = options?.enabled ?? true;

  /**
   * 이 모달이 히스토리 엔트리를 push했는지 추적하는 ref
   *
   * 용도:
   * - 각 모달 인스턴스가 자신이 히스토리를 push했는지 기억
   * - 중첩 모달에서 첫 번째 모달만 cleanup하도록 구분
   *
   * 값의 의미:
   * - true: 이 모달이 첫 번째 모달이었고 히스토리 엔트리를 추가한 상태
   * - false: 중첩 모달이거나 아직 엔트리를 추가하지 않은 상태
   *
   * 예시:
   * - 첫 번째 모달: pushedRef.current = true (히스토리 push함)
   * - 두 번째 모달: pushedRef.current = false (히스토리 push 안 함)
   */
  const pushedRef = useRef(false);

  /**
   * 최신 open/onClose 값을 안전하게 참조하기 위한 ref
   *
   * 문제 상황:
   * - popstate 이벤트 핸들러는 useEffect 내부에서 생성되고 window에 등록됨
   * - 이 핸들러는 컴포넌트가 리렌더링되어도 동일한 함수 참조를 유지해야 함
   * - 하지만 useEffect의 클로저로 인해 오래된 open/onClose 값을 참조할 수 있음
   *
   * 해결 방법:
   * - ref를 사용하여 항상 최신 값을 참조할 수 있도록 함
   * - openRef: 항상 최신 open 상태를 참조
   * - onCloseRef: 항상 최신 onClose 함수를 참조
   *
   * 대안:
   * - open/onClose를 useEffect 의존성에 추가하여 핸들러를 재생성할 수 있음
   * - 하지만 이는 불필요한 리스너 재등록을 유발하므로 ref 방식이 더 효율적
   */
  const openRef = useRef(open);
  const onCloseRef = useRef(onClose);

  /**
   * 이전 open 상태를 추적하는 ref
   *
   * 용도:
   * - 모달이 실제로 열렸는지/닫혔는지 확인
   * - useEffect는 open이 변경될 때마다 실행되므로,
   *   실제로 상태가 변경되었는지 확인하기 위해 필요
   *
   * 초기값:
   * - undefined: 첫 렌더링임을 나타냄
   * - boolean: 이전 open 상태
   *
   * 예시:
   * - 첫 렌더링: prevOpenRef.current = undefined, open = true → 모달 열기 처리
   * - 이전: open = false, 현재: open = true → 모달이 열림
   * - 이전: open = true, 현재: open = false → 모달이 닫힘
   * - 이전: open = true, 현재: open = true → 상태 변경 없음 (리렌더링만 발생)
   */
  const prevOpenRef = useRef<boolean | undefined>(undefined);

  /**
   * open과 onClose가 변경될 때마다 ref를 업데이트
   *
   * 목적:
   * - popstate 이벤트 핸들러에서 항상 최신 값을 참조할 수 있도록 함
   * - 클로저 문제를 해결하여 오래된 값을 참조하는 것을 방지
   *
   * 실행 시점:
   * - open prop이 변경될 때마다
   * - onClose prop이 변경될 때마다
   */
  useEffect(() => {
    openRef.current = open;
    onCloseRef.current = onClose;
  }, [open, onClose]);

  /**
   * 모달 열림/닫힘 처리 및 popstate 이벤트 리스너 등록
   *
   * 주요 동작:
   * 1. 모달이 열릴 때: 히스토리에 엔트리 추가 (첫 번째 모달인 경우)
   * 2. 모달이 닫힐 때: 카운터 감소 및 히스토리 cleanup (마지막 모달인 경우)
   * 3. popstate 이벤트 핸들러 등록: 뒤로가기 버튼 감지
   *
   * 중첩 모달 처리:
   * - 첫 번째 모달만 히스토리에 엔트리 추가
   * - 중첩 모달이 닫혀도 카운터는 정상적으로 감소
   * - 마지막 모달이 닫힐 때만 히스토리 cleanup 실행
   *
   * 실행 시점:
   * - 컴포넌트 마운트 시
   * - enabled 변경 시
   * - open prop 변경 시
   */
  useEffect(() => {
    // 훅이 비활성화되어 있으면 아무것도 하지 않음
    if (!enabled) return;

    // 이전 open 상태와 현재 open 상태 비교
    const wasOpen = prevOpenRef.current;
    const isOpen = open;

    /**
     * 모달 열기 처리
     * 조건: 첫 렌더링이거나 이전에는 닫혀있었고(wasOpen !== true), 지금은 열림(isOpen === true)
     *
     * 예시:
     * - 첫 렌더링: wasOpen = undefined, isOpen = true → 모달 열기 처리
     * - 이전: open = false, 현재: open = true → 모달 열기 처리
     * - 리렌더링: wasOpen = true, isOpen = true → 처리 안 함 (이미 열려있음)
     */
    if (isOpen && wasOpen !== true) {
      handleModalOpen(pushedRef);
    }

    /**
     * 모달 닫기 처리
     * 조건: 이전에는 열려있었고(wasOpen === true), 지금은 닫힘(isOpen === false)
     *
     * 중요:
     * - pushedRef.current와 관계없이 항상 실행
     * - 중첩 모달(두 번째 모달)도 닫힐 때 카운터가 정상적으로 감소해야 함
     * - handleModalClose 내부에서 pushedRef를 확인하여 히스토리 cleanup 여부 결정
     *
     * 예시:
     * - 첫 번째 모달 닫기: wasOpen = true, isOpen = false, pushedRef = true
     *   → 카운터 감소, 히스토리 cleanup 실행
     * - 두 번째 모달 닫기: wasOpen = true, isOpen = false, pushedRef = false
     *   → 카운터 감소, 히스토리 cleanup 안 함 (첫 번째 모달이 아직 열려있으므로)
     */
    if (!isOpen && wasOpen === true) {
      handleModalClose(pushedRef);
    }

    // 이전 상태를 현재 상태로 업데이트 (다음 렌더링을 위해)
    prevOpenRef.current = open;

    /**
     * popstate 이벤트 핸들러 생성 및 등록
     *
     * 동작:
     * - 브라우저 뒤로가기 버튼 클릭 시 발생하는 popstate 이벤트를 감지
     * - 모달이 열려있고, 이 모달이 히스토리를 push했다면 모달만 닫기
     * - 라우트는 변경하지 않음 (모달은 URL을 바꾸지 않으므로)
     */
    const onPopState = createPopStateHandler(openRef, onCloseRef, pushedRef);
    window.addEventListener('popstate', onPopState);

    /**
     * cleanup에서 사용할 값들을 미리 저장
     *
     * 이유:
     * - cleanup 함수는 useEffect가 실행될 때의 값을 캡처함
     * - ref는 변경될 수 있으므로, cleanup 시점의 값을 미리 저장
     * - 린터 경고 방지 (ref가 cleanup 시점에 변경되었을 수 있다는 경고)
     */
    const currentOpen = open;
    const currentOpenRef = openRef.current;
    const currentPushedRef = pushedRef.current;

    /**
     * Cleanup 함수
     *
     * 실행 시점:
     * - 컴포넌트 언마운트 시
     * - enabled 변경 시
     * - open prop 변경 시 (다음 useEffect 실행 전)
     *
     * 역할:
     * - popstate 이벤트 리스너 제거
     * - 메모리 누수 방지
     *
     * 주의:
     * - 카운터 관리는 handleModalClose에서 처리하므로 여기서는 리스너만 제거
     */
    return () => {
      window.removeEventListener('popstate', onPopState);
      console.log(
        '[Modal Stack] cleanup 실행 - open:',
        currentOpen,
        'openRef.current:',
        currentOpenRef,
        'pushedRef:',
        currentPushedRef
      );
    };
  }, [enabled, open]);
}
